import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FloppyDisk, ArrowCounterClockwise, TextAa } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_LABELS = {
  link_simular: "Link 'Simular'", link_planos: "Link 'Coberturas'", btn_login: "Botão de acesso ao painel",
  eyebrow: "Chapéu (texto pequeno acima do título)", title: "Título", title_highlight: "Trecho do título em destaque (verde)",
  subtitle: "Subtítulo / parágrafo", cta_simular: "Botão 'Simular'", cta_painel: "Botão 'Acessar Painel'",
  badges: "Selos (um por linha)", image_url: "URL da imagem", card_eyebrow: "Cartão flutuante — chapéu",
  card_value: "Cartão flutuante — valor", card_unit: "Cartão flutuante — unidade", card_badge: "Cartão flutuante — selo",
  bullets: "Lista de benefícios (um por linha)", left: "Texto à esquerda", right: "Texto à direita",
  items: "Planos",
};
const LONG = new Set(["subtitle"]);
const LIST = new Set(["badges", "bullets", "features"]);

export function SiteContentEditor() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-500 text-sm">Carregando…</div>;
  return (
    <div className="space-y-4" data-testid="site-content-editor">
      <p className="text-sm text-slate-600 max-w-2xl">Edite os textos do site público bloco a bloco. Cada bloco salva separadamente e as alterações aparecem na hora para os visitantes.</p>
      {data.meta.map((m) => (
        <BlockCard key={m.key} blockKey={m.key} label={m.label} value={data.content[m.key]} defaults={data.defaults[m.key]}
          onSaved={(v) => setData({ ...data, content: { ...data.content, [m.key]: v } })} />
      ))}
    </div>
  );
}

function BlockCard({ blockKey, label, value, defaults, onSaved }) {
  const [form, setForm] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(value), [value]);
  const dirty = JSON.stringify(form) !== JSON.stringify(value);

  const save = async (payload = form) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/content/${blockKey}`, payload);
      onSaved(data);
      toast.success(payload && Object.keys(payload).length ? `Bloco "${label}" salvo` : `Bloco "${label}" restaurado`);
    } catch { toast.error("Falha ao salvar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-5" data-testid={`content-block-${blockKey}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="font-display text-xl tracking-tight flex items-center gap-2"><TextAa size={18} className="text-[var(--emerald)]" /> {label}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => save({})} data-testid={`content-reset-${blockKey}`}><ArrowCounterClockwise size={14} className="mr-1" /> Restaurar padrão</Button>
          <Button size="sm" className="btn-primary" disabled={busy || !dirty} onClick={() => save()} data-testid={`content-save-${blockKey}`}><FloppyDisk size={14} className="mr-1" /> Salvar</Button>
        </div>
      </div>
      {blockKey === "plans" ? (
        <PlansFields form={form} setForm={setForm} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {Object.keys(defaults).map((k) => <Field key={k} k={k} form={form} setForm={setForm} testId={`content-${blockKey}-${k}`} />)}
        </div>
      )}
    </div>
  );
}

function Field({ k, form, setForm, testId }) {
  const lbl = FIELD_LABELS[k] || k;
  const set = (v) => setForm({ ...form, [k]: v });
  if (LIST.has(k)) {
    return (
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500 mb-1.5 block">{lbl}</Label>
        <Textarea data-testid={testId} rows={3} value={(form[k] || []).join("\n")} onChange={(e) => set(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
      </div>
    );
  }
  if (LONG.has(k)) {
    return (
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500 mb-1.5 block">{lbl}</Label>
        <Textarea data-testid={testId} rows={3} value={form[k] || ""} onChange={(e) => set(e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs text-slate-500 mb-1.5 block">{lbl}</Label>
      <Input data-testid={testId} value={form[k] ?? ""} onChange={(e) => set(e.target.value)} />
    </div>
  );
}

function PlansFields({ form, setForm }) {
  const setItem = (i, patch) => setForm({ ...form, items: form.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Field k="eyebrow" form={form} setForm={setForm} testId="content-plans-eyebrow" />
        <Field k="title" form={form} setForm={setForm} testId="content-plans-title" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {form.items.map((p, i) => (
          <div key={i} className="p-3 rounded-md border border-[var(--line)] bg-slate-50 space-y-2" data-testid={`content-plan-${i}`}>
            <div className="flex items-center justify-between">
              <div className="eyebrow">Plano {i + 1}</div>
              <label className="flex items-center gap-2 text-xs text-slate-600">Destaque <Switch checked={!!p.highlight} onCheckedChange={(v) => setItem(i, { highlight: v })} data-testid={`content-plan-${i}-highlight`} /></label>
            </div>
            <Input data-testid={`content-plan-${i}-name`} value={p.name} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="Nome" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid={`content-plan-${i}-price`} value={p.price} onChange={(e) => setItem(i, { price: e.target.value })} placeholder="9%" />
              <Input data-testid={`content-plan-${i}-mult`} value={p.mult} onChange={(e) => setItem(i, { mult: e.target.value })} placeholder="até 30x" />
            </div>
            <Input data-testid={`content-plan-${i}-exit`} value={p.exit} onChange={(e) => setItem(i, { exit: e.target.value })} placeholder="Custo de saída" />
            <Input data-testid={`content-plan-${i}-badge`} value={p.badge || ""} onChange={(e) => setItem(i, { badge: e.target.value })} placeholder="Selo (ex.: Mais popular)" />
            <Textarea data-testid={`content-plan-${i}-features`} rows={4} value={(p.features || []).join("\n")} onChange={(e) => setItem(i, { features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} placeholder="Benefícios (um por linha)" />
          </div>
        ))}
      </div>
    </div>
  );
}
